/**
 * azure-functions/shared/compliance/controlContext.cjs
 *
 * CANONICAL COPY (GH #7115). The domain context evaluateControl() is called
 * with, built from a tenant state. src/state/useAppState.js builds the live
 * app's context with this function and the measurement control-status base
 * measure (shared/measurement/measures/controlStatus.cjs) builds the server's
 * with it, so the two cannot drift into two different inputs to the one
 * definition of "compliant".
 *
 *   buildControlDomainContext(state, obligationStatuses)
 *       obligationStatuses — evaluateAllObligations(state.enabledObligations,
 *       state): every enabled obligation including fulfilled ones, which Gate 5
 *       needs to see that an obligation IS linked even when all its criteria
 *       are met.
 *
 * The keys after `frameworks` are read-only extras for the Audit-Readiness
 * Forecast and the assurance-case assembler; evaluateControl ignores them.
 */

'use strict';

const { workflowEvidenceOf } = require('../workflows/workflowEvidence.cjs');
const { realEmployees } = require('../sampleRecords.cjs');
const { OBLIGATIONS_BY_ID } = require('./obligations/index.cjs');

/**
 * PDWB-03 — gate 4's workflow evidence: the overlay as resolved (ok /
 * not-in-plan / unavailable), with what a definition's cadence window is
 * derived from (scheduleFrequency). Exported so a surface that assembles its
 * own domain context spreads the same three keys instead of leaving gate 4
 * blind to workflows (and disagreeing with the app's verdict).
 */
function workflowDomainContext(state) {
  const s = state || {};
  return {
    workflowEvidence: workflowEvidenceOf(s),
    enabledObligations: s.enabledObligations || [],
    obligationsById: OBLIGATIONS_BY_ID,
  };
}

function buildControlDomainContext(state, obligationStatuses) {
  const s = state || {};
  return {
    obligations: obligationStatuses || [],
    artefacts: s.artefacts || [],
    risks: s.risks || [],
    riskObligations: s.riskObligations || [],
    // Sample people never count in any gate (GH #7870). resolveReaders still
    // sees a sample named as a specific reader, via excludedSampleIds().
    employees: realEmployees(s.employees || []),
    // Folded into every document's readers until RDRA-04's migration has run
    // (DOC-B-026, design §d.1); gate 3 requires it.
    documentAssignments: Array.isArray(s.documentAssignments) ? s.documentAssignments : [],
    documentTypes: s.documentTypes || [],
    controlOwnership: s.controlOwnership || {},
    ownershipSettings: s.ownershipSettings || {},
    obligationDecisions: s.obligationDecisions || {},
    frameworks: s.frameworks || [],
    ...workflowDomainContext(s),
    // Read-only extra for the A3 predicted-state forecast over evidence nodes:
    // the external-URL artefact verification interval. evaluateControl ignores
    // unknown keys, so this is inert for the live gate path.
    artefactSettings: s.artefactSettings || {},
    // Read-only extras consumed only by the B1 assurance-case assembler
    // (effectiveness rating → operating leg; integrations + engine run-log →
    // monitored leg). evaluateControl ignores these unknown keys, so they are
    // inert for the live gate path (same contract as artefactSettings above).
    controlEffectiveness: s.controlEffectiveness || {},
    integrations: s.integrations || {},
    engineRunLog: s.engineRunLog || null,
  };
}

module.exports = { buildControlDomainContext, workflowDomainContext };
